/* LUCIENNE Product hero — variant selector + galería + guía tallas + bundle + sticky pro */
(function () {
  'use strict';

  class LucienneProduct extends HTMLElement {
    constructor() {
      super();
      this.product = null;
      this.currentVariant = null;
    }

    connectedCallback() {
      try {
        const dataEl = this.querySelector('[data-product-json]');
        this.product = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('LUCIENNE: no se pudo leer el JSON del producto', e);
        return;
      }

      /* Option metadata viene del Liquid sin truncado para que aunque
         {{ product | json }} corte variantes a 250, los swatches/tallas
         de la UI siempre tengan la lista completa de valores. */
      try {
        const optsEl = this.querySelector('[data-product-options]');
        if (optsEl) {
          const optsData = JSON.parse(optsEl.textContent);
          if (Array.isArray(optsData.names)) this.product.options = optsData.names;
          if (Array.isArray(optsData.values)) this.optionValuesFull = optsData.values;
        }
      } catch (e) { /* non-fatal */ }

      this.currentVariantId = parseInt(this.dataset.selectedVariant, 10) || this.product.variants[0].id;
      this.currentVariant = this.product.variants.find(v => v.id === this.currentVariantId) || this.product.variants[0];

      this.gallery = this.querySelector('[data-gallery]');
      this.priceEl = this.querySelector('[data-price]');
      this.comparePriceEl = this.querySelector('[data-compare-price]');
      this.savingEl = this.querySelector('[data-saving]');
      this.addBtn = this.querySelector('[data-add]') || this.querySelector('[data-bundle-add]');
      this.form = this.querySelector('form[data-product-form]');
      this.stickyBar = this.querySelector('[data-sticky-bar]');
      this.observeTarget = this.querySelector('[data-bundle-add]') || this.querySelector('form[data-product-form]');

      /* Storefront API config */
      this.sfToken = (this.dataset.storefrontToken || '').trim();
      this.sfVersion = (this.dataset.storefrontVersion || '2024-10').trim();
      this.shopDomain = (this.dataset.shopDomain || window.location.host).trim();
      this.currencyCode = (this.dataset.currencyCode || 'EUR').trim();
      this.totalVariantsExpected = parseInt(this.dataset.variantsCount, 10) || this.product.variants.length;
      this._variantIndex = null;
      this._sfLookupCache = new Map();
      this._sfPending = new Map();

      /* Detect option indices */
      this.modeloIdx = this.product.options.findIndex(o =>
        o.toLowerCase().includes('modelo') || o.toLowerCase().includes('hebilla')
      );
      this.colorIdx = this.product.options.findIndex(o =>
        o.toLowerCase().includes('olor')
      );
      this.sizeIdx = this.product.options.findIndex(o =>
        o.toLowerCase().includes('alla') || o.toLowerCase().includes('ize')
      );

      this.bindEvents();
      this._buildVariantIndex();
      this.filterColorsByModelo();
      this.filterGalleryByModelo();
      this.updateUI();
      this.initBundle();
      this.observeSticky();

      const isTruncated = this.product.variants.length >= 250
        || this.totalVariantsExpected > this.product.variants.length;

      if (isTruncated) {
        if (this.totalVariantsExpected > 250 && !this.sfToken) {
          console.warn(
            'LUCIENNE: producto con ' + this.totalVariantsExpected + ' variantes pero ' +
            'Storefront API token no configurado. Las variantes >250 no se podrán seleccionar. ' +
            'Configurá el token en Customizer → Theme settings → Storefront API.'
          );
        }
        this.loadFullVariants();
      }
    }

    /* ========== VARIANT INDEX ========== */

    _buildVariantIndex() {
      const idx = new Map();
      this.product.variants.forEach(v => {
        idx.set(this._optionsKey(v.options), v);
      });
      this._variantIndex = idx;
    }

    _optionsKey(values) {
      return values.map(v => (v == null ? '' : String(v))).join('||');
    }

    _findVariantLocal(selected) {
      if (!this._variantIndex) return null;
      return this._variantIndex.get(this._optionsKey(selected)) || null;
    }

    /* ========== STOREFRONT API CLIENT ========== */

    async _sfRequest(query, variables) {
      if (!this.sfToken) throw new Error('no-sf-token');
      const url = 'https://' + this.shopDomain + '/api/' + this.sfVersion + '/graphql.json';

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.sfToken
        },
        body: JSON.stringify({ query, variables })
      });

      if (!res.ok) throw new Error('sf-http-' + res.status);

      const json = await res.json();

      if (json.errors) {
        console.warn('LUCIENNE Storefront errors', json.errors);
        throw new Error('sf-graphql-error');
      }

      return json.data;
    }

    _sfConvertVariant(node) {
      const numId = parseInt(String(node.id).split('/').pop(), 10);
      const optsMap = {};

      (node.selectedOptions || []).forEach(o => {
        optsMap[o.name] = o.value;
      });

      const opts = (this.product.options || []).map(n => optsMap[n] != null ? optsMap[n] : null);
      const price = node.price ? Math.round(parseFloat(node.price.amount) * 100) : 0;

      const compareAt = node.compareAtPrice && node.compareAtPrice.amount
        ? Math.round(parseFloat(node.compareAtPrice.amount) * 100)
        : null;

      const image = node.image && node.image.url
        ? {
            id: parseInt(String(node.image.id || '').split('/').pop(), 10) || null,
            src: node.image.url
          }
        : null;

      return {
        id: numId,
        title: node.title,
        options: opts,
        available: !!node.availableForSale,
        inventory_management: node.quantityAvailable != null ? 'shopify' : null,
        inventory_quantity: node.quantityAvailable != null ? node.quantityAvailable : null,
        price: price,
        compare_at_price: compareAt,
        featured_image: image,
        sku: node.sku || null
      };
    }

    async _sfFetchAllVariants() {
      const handle = this.product.handle || this.dataset.productHandle;
      if (!handle) return null;

      const query = `
        query GetAllVariants($handle: String!, $first: Int!, $after: String) {
          product(handle: $handle) {
            variants(first: $first, after: $after) {
              edges { cursor node {
                id title sku availableForSale quantityAvailable
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                selectedOptions { name value }
                image { id url }
              } }
              pageInfo { hasNextPage endCursor }
            }
          }
        }`;

      const all = [];
      let after = null;
      let safety = 20;

      while (safety-- > 0) {
        const data = await this._sfRequest(query, { handle, first: 250, after });
        const conn = data && data.product && data.product.variants;

        if (!conn) break;

        conn.edges.forEach(e => all.push(this._sfConvertVariant(e.node)));

        if (!conn.pageInfo.hasNextPage) break;

        after = conn.pageInfo.endCursor;
      }

      return all;
    }

    async _sfLookupSingle(selected) {
      const key = this._optionsKey(selected);

      if (this._sfLookupCache.has(key)) return this._sfLookupCache.get(key);
      if (this._sfPending.has(key)) return this._sfPending.get(key);
      if (!this.sfToken) return null;

      const handle = this.product.handle || this.dataset.productHandle;
      if (!handle) return null;

      const selectedOptions = this.product.options.map((name, i) => ({
        name,
        value: selected[i] != null ? String(selected[i]) : ''
      }));

      const query = `
        query GetVariantByOpts($handle: String!, $opts: [SelectedOptionInput!]!) {
          product(handle: $handle) {
            variantBySelectedOptions(selectedOptions: $opts) {
              id title sku availableForSale quantityAvailable
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              selectedOptions { name value }
              image { id url }
            }
          }
        }`;

      const promise = (async () => {
        try {
          const data = await this._sfRequest(query, { handle, opts: selectedOptions });
          const node = data && data.product && data.product.variantBySelectedOptions;

          if (!node) {
            this._sfLookupCache.set(key, null);
            return null;
          }

          const v = this._sfConvertVariant(node);
          this._sfLookupCache.set(key, v);

          if (!this._variantIndex.has(this._optionsKey(v.options))) {
            this.product.variants.push(v);
            this._variantIndex.set(this._optionsKey(v.options), v);
          }

          return v;
        } catch (err) {
          this._sfLookupCache.set(key, null);
          return null;
        } finally {
          this._sfPending.delete(key);
        }
      })();

      this._sfPending.set(key, promise);
      return promise;
    }

    async loadFullVariants() {
      if (this.sfToken) {
        try {
          const all = await this._sfFetchAllVariants();

          if (all && all.length > this.product.variants.length) {
            this._applyFullVariants(all, 'storefront-api');
            return;
          }
        } catch (err) {
          console.warn('LUCIENNE: Storefront API falló, intentando fallback', err);
        }
      }

      const handle = this.product.handle || this.dataset.productHandle;
      if (!handle) return;

      const allVariants = [];
      const seen = new Set();
      let page = 1;
      let totalPages = 1;

      try {
        do {
          const res = await fetch(
            '/products/' + handle + '?view=variants-json&page=' + page,
            {
              credentials: 'same-origin',
              headers: { 'Accept': 'application/json' }
            }
          );

          if (!res.ok) break;

          const text = await res.text();
          const data = JSON.parse(text.trim());

          if (!data || !Array.isArray(data.variants)) break;

          let added = 0;

          data.variants.forEach(v => {
            if (!seen.has(v.id)) {
              seen.add(v.id);
              allVariants.push(v);
              added++;
            }
          });

          totalPages = data.total_pages || 1;

          if (added === 0 && page > 1) break;

          page++;
        } while (page <= totalPages && page <= 20);

        if (allVariants.length > this.product.variants.length) {
          this._applyFullVariants(allVariants, 'paginated-template');
        }
      } catch (err) {
        console.warn('LUCIENNE: fallback paginado falló', err);
      }
    }

    _applyFullVariants(allVariants, source) {
      this.product.variants = allVariants;
      this._buildVariantIndex();
      this.filterColorsByModelo();
      this.filterGalleryByModelo();

      const selected = this._getSelectedOptions();
      const match = this._findVariantLocal(selected);

      if (match) {
        this._variantUnavailable = false;
        this.currentVariant = match;
        this.currentVariantId = match.id;
        this.updateUI();
      } else {
        const fresh = this.product.variants.find(v => v.id === this.currentVariantId);
        if (fresh) {
          this.currentVariant = fresh;
          this.updateUI();
        }
      }

      this.updateSizeStockStates();
      console.log('LUCIENNE: variantes completas (' + source + ') →', allVariants.length);
    }

    bindEvents() {
      this.querySelectorAll('[data-option-input]').forEach(input => {
        input.addEventListener('change', () => this.onOptionChange(input));
      });

      this.querySelectorAll('[data-gallery-thumb]').forEach(thumb => {
        thumb.addEventListener('click', e => {
          e.preventDefault();
          const idx = parseInt(thumb.dataset.galleryThumb, 10);
          this.goToImage(idx);
        });
      });

      const sizeGuideBtn = this.querySelector('[data-size-guide-open]');
      const modal = document.querySelector('[data-size-guide-modal]');

      if (sizeGuideBtn && modal) {
        sizeGuideBtn.addEventListener('click', e => {
          e.preventDefault();
          modal.removeAttribute('hidden');
          modal.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });

        modal.querySelectorAll('[data-size-guide-close]').forEach(btn => {
          btn.addEventListener('click', () => {
            modal.classList.remove('is-open');
            modal.setAttribute('hidden', '');
            document.body.style.overflow = '';
          });
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', e => {
          e.preventDefault();
          this.ajaxAddToCart();
        });
      }

      this.querySelectorAll('[data-sticky-option]').forEach(sel => {
        sel.addEventListener('change', () => {
          const idx = parseInt(sel.dataset.stickyOption, 10);
          const mainInput = this.querySelector(
            `[data-option-input][data-option-index="${idx}"][value="${CSS.escape(sel.value)}"]`
          );

          if (mainInput) {
            mainInput.checked = true;
            this.onOptionChange(mainInput);
          }
        });
      });

      this.stickyQty = 1;

      const stickyQtySel = this.querySelector('[data-sticky-qty]');

      if (stickyQtySel) {
        stickyQtySel.addEventListener('change', () => {
          this.stickyQty = parseInt(stickyQtySel.value, 10) || 1;
        });
      }

      const stickyAddBtn = this.querySelector('[data-sticky-add]');

      if (stickyAddBtn) {
        stickyAddBtn.addEventListener('click', () => {
          this.ajaxAddToCart(this.stickyQty);
        });
      }
    }

    /* ========== MODELO / HEBILLAS FILTERING ========== */

    filterColorsByModelo() {
      if (this.modeloIdx < 0 || this.colorIdx < 0) return;

      const modeloInput = this.querySelector(
        `[data-option-input][data-option-index="${this.modeloIdx}"]:checked`
      );

      if (!modeloInput) return;

      const selectedModelo = modeloInput.value;
      const availableColors = new Set();

      this.product.variants.forEach(v => {
        if (v.options[this.modeloIdx] === selectedModelo) {
          availableColors.add(v.options[this.colorIdx]);
        }
      });

      let currentColorAvailable = false;
      let firstAvailableInput = null;

      this.querySelectorAll(
        `[data-option-input][data-option-index="${this.colorIdx}"]`
      ).forEach(input => {
        const swatch = input.closest('.lucienne-pdp__swatch');
        if (!swatch) return;

        const isAvailable = availableColors.has(input.value);
        swatch.style.display = isAvailable ? '' : 'none';

        if (isAvailable) {
          if (!firstAvailableInput) firstAvailableInput = input;
          if (input.checked) currentColorAvailable = true;
        }
      });

      if (!currentColorAvailable && firstAvailableInput) {
        const checked = this.querySelector(
          `[data-option-input][data-option-index="${this.colorIdx}"]:checked`
        );

        if (checked) checked.checked = false;

        firstAvailableInput.checked = true;
      }

      this.querySelectorAll(`[data-sticky-option="${this.colorIdx}"] option`).forEach(opt => {
        const show = availableColors.has(opt.value);
        opt.style.display = show ? '' : 'none';
        opt.disabled = !show;
      });

      if (this.bundleEl) {
        this.bundleEl.querySelectorAll(`[data-item-option="${this.colorIdx}"] option`).forEach(opt => {
          const show = availableColors.has(opt.value);
          opt.style.display = show ? '' : 'none';
          opt.disabled = !show;
        });
      }
    }

    filterGalleryByModelo() {
      if (this.modeloIdx < 0 || !this.gallery) return;

      const modeloInput = this.querySelector(
        `[data-option-input][data-option-index="${this.modeloIdx}"]:checked`
      );

      if (!modeloInput) return;

      const selectedModelo = modeloInput.value;
      const modeloImageIds = new Set();

      this.product.variants.forEach(v => {
        if (v.options[this.modeloIdx] === selectedModelo && v.featured_image) {
          modeloImageIds.add(v.featured_image.id);
        }
      });

      if (modeloImageIds.size === 0) return;

      const slides = this.gallery.querySelectorAll('[data-gallery-slide]');
      let firstVisibleIdx = -1;

      slides.forEach((slide, i) => {
        const imgId = parseInt(slide.dataset.imageId, 10);
        const show = modeloImageIds.has(imgId);

        slide.style.display = show ? '' : 'none';

        if (show && firstVisibleIdx < 0) firstVisibleIdx = i;
        if (!show) slide.classList.remove('is-active');
      });

      this.querySelectorAll('[data-gallery-thumb]').forEach(thumb => {
        const imgId = parseInt(thumb.dataset.imageId, 10);
        const show = modeloImageIds.has(imgId);

        thumb.style.display = show ? '' : 'none';

        if (!show) thumb.classList.remove('is-active');
      });

      const anyActive = this.gallery.querySelector('[data-gallery-slide].is-active:not([style*="display: none"])');

      if (!anyActive && firstVisibleIdx >= 0) {
        this.goToImage(firstVisibleIdx);
      }
    }

    /* ========== OPTION CHANGE ========== */

    async onOptionChange(changedInput) {
      if (changedInput && parseInt(changedInput.dataset.optionIndex, 10) === this.modeloIdx) {
        this.filterColorsByModelo();
        this.filterGalleryByModelo();
      }

      const selected = this._getSelectedOptions();
      let match = this._findVariantLocal(selected);

      if (!match && this.sfToken) {
        this._setVariantPending(true);
        match = await this._sfLookupSingle(selected);

        const stillSelected = this._getSelectedOptions();

        if (stillSelected.join('||') !== selected.join('||')) return;

        this._setVariantPending(false);
      }

      if (match) {
        this._variantUnavailable = false;
        this.currentVariant = match;
        this.currentVariantId = match.id;
        this.updateUI();
      } else {
        this._showUnavailable();
      }
    }

    _getSelectedOptions() {
      const selected = [];

      this.querySelectorAll('[data-option-input]:checked').forEach(input => {
        const i = parseInt(input.dataset.optionIndex, 10);
        selected[i] = input.value;
      });

      for (let i = 0; i < (this.product.options || []).length; i++) {
        if (selected[i] == null) selected[i] = null;
      }

      return selected;
    }

    _setVariantPending(isPending) {
      if (!this.addBtn) return;

      if (isPending) {
        this._prevBtnText = this._prevBtnText || this.addBtn.textContent;
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Verificando...';
      } else {
        this.addBtn.disabled = false;

        if (this._prevBtnText) {
          this.addBtn.textContent = this._prevBtnText;
          this._prevBtnText = null;
        }
      }
    }

    _showUnavailable() {
      this._variantUnavailable = true;

      if (this.addBtn) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Combinación no disponible';
      }

      this.querySelectorAll('[data-option-input]:checked').forEach(input => {
        const idx = input.dataset.optionIndex;
        const textEl = this.querySelector(`[data-option-selected-${idx}]`);

        if (textEl) textEl.textContent = input.value;
      });
    }

    updateUI() {
      const v = this.currentVariant;

      if (this.priceEl) this.priceEl.textContent = this.formatMoney(v.price);

      if (this.comparePriceEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          this.comparePriceEl.textContent = this.formatMoney(v.compare_at_price);
          this.comparePriceEl.hidden = false;

          if (this.savingEl) {
            const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
            this.savingEl.textContent = '-' + pct + '%';
            this.savingEl.hidden = false;
          }
        } else {
          this.comparePriceEl.hidden = true;

          if (this.savingEl) this.savingEl.hidden = true;
        }
      }

      this.querySelectorAll('[data-option-input]:checked').forEach(input => {
        const idx = input.dataset.optionIndex;
        const textEl = this.querySelector(`[data-option-selected-${idx}]`);

        if (textEl) textEl.textContent = input.value;
      });

      const variantIdInput = this.querySelector('input[name="id"]');

      if (variantIdInput) variantIdInput.value = v.id;

      if (this.addBtn) {
        if (v.available) {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.labelAdd || 'Añadir al carrito';
        } else {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.labelSold || 'Agotado';
        }
      }

      if (v.featured_image && v.featured_image.id && this.gallery) {
        const slides = this.gallery.querySelectorAll('[data-gallery-slide]');
        let targetIdx = -1;

        slides.forEach((s, i) => {
          if (s.style.display === 'none') return;
          if (parseInt(s.dataset.imageId, 10) === v.featured_image.id) targetIdx = i;
        });

        if (targetIdx < 0 && v.featured_image.src) {
          const filename = v.featured_image.src.split('/').pop().split('?')[0].split('.')[0];

          if (filename) {
            slides.forEach((s, i) => {
              if (s.style.display === 'none') return;

              const img = s.querySelector('img');

              if (img && img.src && img.src.includes(filename)) targetIdx = i;
            });
          }
        }

        if (targetIdx >= 0) this.goToImage(targetIdx);
      }

      this.updateSizeStockStates();
      this.updateStickyBar();

      if (this.bundleEl) {
        this.bundleEl.querySelectorAll('[data-item-option]').forEach(sel => {
          const optIdx = parseInt(sel.dataset.itemOption, 10);

          if (v.options[optIdx]) sel.value = v.options[optIdx];
        });
      }

      this.recalcBundle();
    }

    /* ========== SIZE STOCK STATES — indexed matching ========== */

    updateSizeStockStates() {
      if (this.sizeIdx < 0) return;

      const selectedModelo = this.modeloIdx >= 0
        ? this._getCheckedValue(this.modeloIdx)
        : null;

      const selectedColor = this.colorIdx >= 0
        ? this._getCheckedValue(this.colorIdx)
        : null;

      this.querySelectorAll('[data-size-option]').forEach(label => {
        const sizeValue = label.dataset.sizeOption;

        const match = this.product.variants.find(v => {
          if (v.options[this.sizeIdx] !== sizeValue) return false;
          if (selectedModelo && v.options[this.modeloIdx] !== selectedModelo) return false;
          if (selectedColor && v.options[this.colorIdx] !== selectedColor) return false;

          return true;
        });

        const badge = label.querySelector('[data-stock-badge]');

        label.classList.remove('is-soldout', 'is-low');

        if (badge) badge.textContent = '';

        if (!match) {
          /* Variant not found — could be truncated JSON. Leave as available. */
        } else if (!match.available) {
          label.classList.add('is-soldout');

          if (badge) badge.textContent = 'Agotado';
        } else if (match.inventory_management && match.inventory_quantity > 0 && match.inventory_quantity <= 3) {
          label.classList.add('is-low');

          if (badge) badge.textContent = 'Quedan ' + match.inventory_quantity;
        }
      });
    }

    _getCheckedValue(optionIndex) {
      const input = this.querySelector(
        `[data-option-input][data-option-index="${optionIndex}"]:checked`
      );

      return input ? input.value : null;
    }

    /* ========== STICKY BAR ========== */

    updateStickyBar() {
      const v = this.currentVariant;

      this.querySelectorAll('[data-sticky-option]').forEach(sel => {
        const idx = parseInt(sel.dataset.stickyOption, 10);

        if (v.options[idx] != null) sel.value = v.options[idx];
      });

      const stickyImg = this.querySelector('.lucienne-sticky-pro__img');

      if (!stickyImg) return;

      let src = null;

      if (v.featured_image && v.featured_image.src) {
        src = v.featured_image.src;
      }

      if (!src && this.product.featured_image) {
        src = typeof this.product.featured_image === 'string'
          ? this.product.featured_image
          : this.product.featured_image.src;
      }

      if (!src && this.product.images && this.product.images.length) {
        const first = this.product.images[0];
        src = typeof first === 'string' ? first : first.src;
      }

      if (src) {
        stickyImg.src = src.replace(/\?.*/, '') + '?width=120';
        stickyImg.srcset = '';
        stickyImg.removeAttribute('srcset');
      }
    }

    getSelectedOption(optionName) {
      const idx = this.product.options.findIndex(o => o.toLowerCase().includes(optionName));

      if (idx < 0) return null;

      return this._getCheckedValue(idx);
    }

    goToImage(idx) {
      if (!this.gallery) return;

      const slides = this.gallery.querySelectorAll('[data-gallery-slide]');
      const slide = slides[idx];

      if (!slide) return;

      slides.forEach(s => s.classList.remove('is-active'));
      slide.classList.add('is-active');

      const isDesktopGallery = window.matchMedia('(min-width: 990px)').matches;

      if (isDesktopGallery) {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        this.gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }

      this.querySelectorAll('[data-gallery-thumb]').forEach(t => {
        t.classList.toggle('is-active', parseInt(t.dataset.galleryThumb, 10) === idx);
      });
    }

    /* ========== STICKY BAR PRO ========== */

    observeSticky() {
      if (!this.stickyBar || !this.observeTarget) return;

      const io = new IntersectionObserver(entries => {
        entries.forEach(e => {
          const gone = !e.isIntersecting && e.boundingClientRect.top < 0;

          this.stickyBar.classList.toggle('is-visible', gone);

          if (gone) this.stickyBar.removeAttribute('hidden');
        });
      }, { threshold: 0 });

      io.observe(this.observeTarget);
    }

    /* ========== BUNDLE ========== */

    initBundle() {
      const bundleEl = this.querySelector('[data-bundle-inline]');

      if (!bundleEl) return;

      this.bundleEl = bundleEl;
      this.bundleTiers = bundleEl.querySelectorAll('[data-bundle-tier]');
      this.bundleAddBtn = bundleEl.querySelector('[data-bundle-add]');

      this.bundleTiers.forEach(tier => {
        tier.addEventListener('click', e => {
          if (e.target.closest('select') || e.target.closest('input[type="radio"]')) return;

          this.selectBundleTier(tier);
        });

        const radio = tier.querySelector('input[name="bundle_tier"]');

        if (radio) {
          radio.addEventListener('change', () => this.selectBundleTier(tier));
        }
      });

      bundleEl.querySelectorAll('[data-item-option]').forEach(sel => {
        sel.addEventListener('change', () => this.recalcBundle());
      });

      if (this.bundleAddBtn) {
        this.bundleAddBtn.addEventListener('click', () => this.bundleAddToCart());
      }

      this.filterColorsByModelo();
      this.recalcBundle();
    }

    selectBundleTier(tier) {
      this.bundleTiers.forEach(t => {
        t.classList.remove('is-selected');

        const r = t.querySelector('input[name="bundle_tier"]');

        if (r) r.checked = false;
      });

      tier.classList.add('is-selected');

      const r = tier.querySelector('input[name="bundle_tier"]');

      if (r) r.checked = true;

      this.recalcBundle();
    }

    recalcBundle() {
      if (!this.bundleTiers) return;

      const basePrice = this.currentVariant.price;
      const comparePrice = this.currentVariant.compare_at_price || basePrice;

      this.bundleTiers.forEach(tier => {
        const qty = parseInt(tier.dataset.qty, 10);
        const disc = parseInt(tier.dataset.discount, 10) || 0;
        const totalCompare = comparePrice * qty;
        const totalFinal = Math.round(basePrice * qty * (1 - disc / 100));
        const saving = totalCompare - totalFinal;

        const priceEl = tier.querySelector('[data-tier-price]');
        const compareEl = tier.querySelector('[data-tier-compare]');
        const saveEl = tier.querySelector('[data-tier-save]');

        if (priceEl) priceEl.textContent = this.formatMoney(totalFinal);

        if (compareEl) {
          compareEl.textContent = this.formatMoney(totalCompare);
          compareEl.style.display = saving > 0 ? '' : 'none';
        }

        if (saveEl) {
          saveEl.textContent = '€' + (saving / 100).toLocaleString('es-ES', {
            minimumFractionDigits: 0
          });
        }
      });
    }

    async bundleAddToCart() {
      const tier = this.bundleEl.querySelector('[data-bundle-tier].is-selected');

      if (!tier) return;

      const qty = parseInt(tier.dataset.qty, 10);
      const disc = parseInt(tier.dataset.discount, 10) || 0;
      const items = [];

      if (qty === 1) {
        if (this._variantUnavailable || !this.currentVariant || !this.currentVariant.id) {
          alert('Esa combinación no está disponible. Probá con otra.');
          return;
        }

        items.push({
          id: this.currentVariant.id,
          quantity: 1
        });
      } else {
        const area = tier.querySelector('[data-tier-selectors]');

        if (!area) return;

        const prevBtnText = this.bundleAddBtn.textContent;

        this.bundleAddBtn.disabled = true;
        this.bundleAddBtn.textContent = 'Verificando...';

        try {
          for (let i = 1; i <= qty; i++) {
            const row = area.querySelector(`[data-item-row="${i}"]`);

            if (!row) continue;

            const opts = Array.from(row.querySelectorAll('select')).map(s => s.value);
            const fullOpts = (this.product.options || []).map((_, idx) => opts[idx] != null ? opts[idx] : null);

            let v = this._findVariantLocal(fullOpts);

            if (!v && this.sfToken) v = await this._sfLookupSingle(fullOpts);

            if (!v) {
              alert(`Combinación no disponible para artículo #${i}`);
              return;
            }

            if (!v.available) {
              alert(`Artículo #${i} agotado en esa combinación`);
              return;
            }

            const existing = items.find(it => it.id === v.id);

            if (existing) {
              existing.quantity++;
            } else {
              items.push({
                id: v.id,
                quantity: 1,
                properties: {
                  '_bundle_qty': qty,
                  '_bundle_discount': disc
                }
              });
            }
          }
        } finally {
          this.bundleAddBtn.disabled = false;
          this.bundleAddBtn.textContent = prevBtnText;
        }
      }

      await this._doAddToCart(items, this.bundleAddBtn);
    }

    /* ========== AJAX ADD TO CART ========== */

    async ajaxAddToCart(qty) {
      if (!this.addBtn || this.addBtn.disabled) return;

      if (this._variantUnavailable || !this.currentVariant || !this.currentVariant.id) {
        alert('Esa combinación no está disponible. Probá con otra.');
        return;
      }

      const items = [
        {
          id: this.currentVariant.id,
          quantity: qty || 1
        }
      ];

      await this._doAddToCart(items, this.addBtn);
    }

    async _doAddToCart(items, btn) {
      if (!items.length) return;

      const originalText = btn.textContent;

      btn.disabled = true;
      btn.textContent = 'Añadiendo...';

      try {
        const drawer = document.querySelector('cart-drawer');

        const sectionsToRequest = drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map(s => s.id)
          : ['cart-drawer', 'cart-icon-bubble'];

        const res = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToRequest,
            sections_url: window.location.pathname
          })
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || 'Cart error');
        }

        const response = await res.json();

        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(response);

          if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');

          const cartItems = drawer.querySelector('cart-drawer-items');

          if (cartItems && cartItems.classList.contains('is-empty')) {
            cartItems.classList.remove('is-empty');
          }
        } else if (drawer) {
          const sectionsRes = await fetch(`${window.routes.cart_url}?sections=cart-drawer`);
          const sectionsData = await sectionsRes.json();

          if (sectionsData['cart-drawer']) {
            const doc = new DOMParser().parseFromString(sectionsData['cart-drawer'], 'text/html');
            const newDrawerInner = doc.querySelector('#CartDrawer');
            const oldDrawerInner = drawer.querySelector('#CartDrawer');

            if (newDrawerInner && oldDrawerInner) {
              oldDrawerInner.innerHTML = newDrawerInner.innerHTML;
            }

            drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
            drawer.classList.remove('is-empty');
            drawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }
        }

        if (response.sections && response.sections['cart-icon-bubble']) {
          const bubbleEl = document.getElementById('cart-icon-bubble');

          if (bubbleEl) {
            const doc = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
            const newBubble = doc.querySelector('.shopify-section');

            if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
          }
        }

        const cartData = await (await fetch(window.routes.cart_url + '.js')).json();

        document.querySelectorAll('.lucienne-header__cart-count').forEach(el => {
          el.textContent = cartData.item_count;
          el.dataset.count = cartData.item_count;
        });

        this.trackAddToCart(items, response, cartData);
        this.publishCartUpdate(items, response, cartData);
      } catch (err) {
        console.error('Add to cart error', err);
        alert('Error al añadir al carrito. Inténtalo de nuevo.');
      } finally {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    }

    getAddedItemsFromResponse(items, response) {
      if (response && Array.isArray(response.items) && response.items.length) {
        return response.items;
      }

      return items.map(item => {
        const variant = this.product.variants.find(v => String(v.id) === String(item.id));

        return {
          id: item.id,
          variant_id: item.id,
          product_id: this.product.id,
          product_title: this.product.title,
          title: variant ? variant.title : this.product.title,
          price: variant ? variant.price : (this.currentVariant ? this.currentVariant.price : 0),
          final_price: variant ? variant.price : (this.currentVariant ? this.currentVariant.price : 0),
          quantity: item.quantity || 1
        };
      });
    }

    trackAddToCart(items, response, cartData) {
      const addedItems = this.getAddedItemsFromResponse(items, response);

      if (!addedItems.length) return;

      const eventId = 'lucienne_atc_' + Date.now() + '_' + Math.random().toString(36).slice(2);
      const currency = this.currencyCode || 'EUR';

      const contents = addedItems.map(item => {
        const unitPrice = Number(item.final_price || item.price || 0) / 100;

        return {
          id: String(item.variant_id || item.id),
          quantity: Number(item.quantity || 1),
          item_price: Number(unitPrice.toFixed(2))
        };
      });

      const value = contents.reduce((sum, item) => {
        return sum + item.item_price * item.quantity;
      }, 0);

      const contentIds = contents.map(item => item.id);
      const firstItem = addedItems[0];

      const fbPayload = {
        content_type: 'product',
        content_ids: contentIds,
        contents,
        content_name: addedItems.length === 1
          ? (firstItem.product_title || firstItem.title || this.product.title)
          : this.product.title,
        value: Number(value.toFixed(2)),
        currency
      };

      if (typeof window.fbq === 'function') {
        try {
          window.fbq('track', 'AddToCart', fbPayload, {
            eventID: eventId
          });
        } catch (err) {
          console.warn('LUCIENNE: no se pudo enviar AddToCart a Meta Pixel', err);
        }
      }

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'add_to_cart',
        event_id: eventId,
        ecommerce: {
          currency,
          value: Number(value.toFixed(2)),
          items: addedItems.map(item => ({
            item_id: String(item.variant_id || item.id),
            item_name: item.product_title || item.title || this.product.title,
            item_variant: item.variant_title || item.title || '',
            price: Number((Number(item.final_price || item.price || 0) / 100).toFixed(2)),
            quantity: Number(item.quantity || 1)
          }))
        }
      });

      document.dispatchEvent(new CustomEvent('lucienne:add-to-cart', {
        detail: {
          eventId,
          items: addedItems,
          cart: cartData || null,
          metaPayload: fbPayload
        }
      }));
    }

    publishCartUpdate(items, response, cartData) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      const firstItem = items[0];
      const addedVariantId = firstItem ? firstItem.id : null;

      try {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'lucienne-product',
          productVariantId: addedVariantId,
          variantId: addedVariantId,
          cartData: cartData || response,
          addedItems: this.getAddedItemsFromResponse(items, response)
        });
      } catch (err) {
        console.warn('LUCIENNE: no se pudo publicar cartUpdate', err);
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return amount + ' €';
    }
  }

  if (!customElements.get('lucienne-product')) {
    customElements.define('lucienne-product', LucienneProduct);
  }

  /* ============ FAQ acordeón con scroll suave ============ */

  document.addEventListener('click', e => {
    const summary = e.target.closest('.lucienne-faq__summary');

    if (!summary) return;

    requestAnimationFrame(() => {
      const details = summary.parentElement;

      if (details.open) {
        const top = details.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      }
    });
  });
})();